import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const preloadedGalleryParents = new WeakSet();

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    if (!preloadedGalleryParents.has(this.parentElement)) {
      preloadedGalleryParents.add(this.parentElement);
      for (const template of this.parentElement.querySelectorAll('template[data-olayz-gallery-template]')) {
        const hero = template.content.querySelector('slideshow-slide .product-media__image');
        if (!hero) continue;
        const image = new Image();
        image.sizes = hero.sizes;
        image.srcset = hero.srcset;
        image.src = hero.src;
      }
    }
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selection = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const scrollToPhotos = Boolean(event.target.closest('variant-picker')) &&
      !event.target.closest('dialog, quick-add-dialog') && !this.closest('dialog');

    const selection = ++this.#selection;
    const picker = event.target.closest('variant-picker');
    const option = picker?.selectedOption;
    const key = this.dataset.olayzGalleryKey;
    const template = key && Array.from(this.parentElement.querySelectorAll('template[data-olayz-gallery-template]'))
      .find((item) => item.dataset.olayzGalleryTemplate === key &&
        (option?.dataset?.variantId
          ? item.dataset.variantId === option.dataset.variantId
          : item.dataset.variantTitle === option?.value));
    const cachedGallery = template?.content.querySelector('media-gallery');
    let visibleGallery = this;
    if (cachedGallery) {
      visibleGallery = cachedGallery.cloneNode(true);
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.replaceWith(visibleGallery);
    }
    if (scrollToPhotos && visibleGallery.isConnected) {
      const headerHeight = document.querySelector('header-component')?.getBoundingClientRect().height || 0;
      const offset = headerHeight + 12;
      const top = visibleGallery.getBoundingClientRect().top;
      if (top < offset) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + top - offset),
          behavior: 'instant',
        });
      }
    }

    event.promise
      .then(({ detail }) => {
        if (cachedGallery || !this.isConnected || selection !== this.#selection) return;
        const newMediaGallery = detail?.html?.querySelector('media-gallery');
        if (newMediaGallery) this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

